import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { searchProducts } from '../../redux/user/shop/shop_service_syn';
import { getPublicCategories } from '../../redux/user/landingpageproducts/landingpage_service_syn';
import { getAllBrands } from '../../redux/user/brands/brands_service_syn';
import {
  updateProductBrand,
  updateProductCategory,
  updateProductName,
} from '../../redux/user/shop/shopSlice';

export default function Sidebar({
  onCategorySelect,
  onBrandSelect,
  onPriceChange,
  searchTerm = '',
  width = 'w-full',
}) {
  const dispatch = useDispatch();
  const { categories } = useSelector((state) => state.landpage || {});
  const { brands } = useSelector((state) => state.brands || {});
  const [priceRange, setPriceRange] = useState({ from: '', to: '' });

  const {
    product_limit,
    product_name,
    loading,
    product_category,
    product_brand,
  } = useSelector((state) => state.shop);

  const categoryList = categories?.docs || [];
  const brandsList = brands?.data?.docs || [];

  useEffect(() => {
    dispatch(getPublicCategories({ page: 0, limit: 100 }));
    dispatch(getAllBrands({ page: 0, limit: 100 }));
  }, []);

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      // Update parent component's search term
      makeProductCall(searchValue, '', '', 0, 0);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    dispatch(updateProductName(value));
    debouncedSearch(value);
  };

  const handlePriceChange = (type, value) => {
    setPriceRange((prev) => ({ ...prev, [type]: value }));
  };

  const handlePriceFilter = () => {
    onPriceChange(priceRange);

    makeProductCall('', '', '', priceRange.from, priceRange.to);
  };

  const makeProductCall = (searchTerm, category, brand, from, to) => {
    dispatch(
      searchProducts({
        name: searchTerm || '""',
        category: category || '""',
        brand: brand || '""',
        from: from || 0,
        to: to || 0,
        page: 0,
        limit: product_limit,
      })
    );
  };

  return (
    <div
      className={`bg-white p-4 rounded-lg shadow ${width} 2xl:h-[66rem] xl:h-[57rem] lg:h-[49rem] md:h-[38rem]`}
    >
      {/* Search */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-4">Search</h2>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search here..."
            value={product_name || ''}
            onChange={handleSearchChange}
            className="flex-1 px-3 w-12 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <button className="bg-[#f51c1c] text-white px-4 rounded-lg hover:bg-gray-800">
            Search
          </button>
        </div>
      </div>

      {/* Categories */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-4">Categories</h2>
        <ul className="space-y-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
          <li>
            <button
              onClick={() => {
                if (loading) return;

                if (product_category === '') return;

                dispatch(updateProductCategory(''));
                makeProductCall('', '', '', priceRange.from, priceRange.to);
              }}
              className={`w-full flex items-center gap-3 px-3 py-1 hover:bg-gray-100 ${
                product_category === '' ? 'bg-gray-100' : ''
              } rounded-lg transition-colors`}
            >
              <span>{'All'}</span>
            </button>
          </li>
          {categoryList.map((category) => (
            <li key={category._id}>
              <button
                onClick={() => {
                  if (loading) return;

                  if (product_category === category.name) return;

                  dispatch(updateProductCategory(category.name));
                  makeProductCall(
                    '',
                    category.name,
                    '',
                    priceRange.from,
                    priceRange.to
                  );
                }}
                className={`w-full flex items-center gap-3 px-3 py-1 hover:bg-gray-100 ${
                  product_category === category.name ? 'bg-gray-100' : ''
                } rounded-lg transition-colors`}
              >
                {category.icon}
                <span>{category.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Brands - New Section */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-4">Brands</h2>
        <ul className="space-y-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
          <li>
            <button
              onClick={() => {
                if (loading) return;

                if (product_brand === '') return;

                dispatch(updateProductBrand(''));
                makeProductCall('', '', '', priceRange.from, priceRange.to);
              }}
              className={`w-full flex items-center gap-3 px-3 py-1 hover:bg-gray-100 ${
                product_brand === '' ? 'bg-gray-100' : ''
              } rounded-lg transition-colors`}
            >
              <span>All</span>
            </button>
          </li>
          {brandsList.map((brand) => (
            <li key={brand._id}>
              <button
                onClick={() => {
                  if (loading) return;

                  if (product_brand === brand.name) return;

                  dispatch(updateProductBrand(brand.name));
                  makeProductCall(
                    '',
                    '',
                    brand.name,
                    priceRange.from,
                    priceRange.to
                  );
                }}
                className={`w-full flex items-center gap-3 px-3 py-1 hover:bg-gray-100 ${
                  product_brand === brand.name ? 'bg-gray-100' : ''
                } rounded-lg transition-colors`}
              >
                {brand.brand_image && (
                  <img
                    src={brand.brand_image}
                    alt={brand.name}
                    className="w-6 h-6 object-contain"
                  />
                )}
                <span>{brand.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Filter By Price */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-4">Filter By Price</h2>
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <input
              type="number"
              placeholder="From"
              value={priceRange.from}
              onChange={(e) => handlePriceChange('from', e.target.value)}
              className="w-24 px-2 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <span>To</span>
            <input
              type="number"
              placeholder="250.00"
              className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              value={priceRange.to}
              onChange={(e) => handlePriceChange('to', e.target.value)}
            />
          </div>
          <button
            className="bg-[#f51c1c] text-white px-4 lg:py-2 rounded-lg hover:bg-gray-800 w-full"
            onClick={handlePriceFilter}
          >
            Filter
          </button>
        </div>
      </div>
    </div>
  );
}
